"use client";
import React from "react";
import Image from "components/common/Image";
import cx from "classnames";

interface AvatarProps {
  src?: string;
  alt?: string;
  name?: string;
  size?: "small" | "medium" | "large" | "xlarge";
  shape?: "circle" | "square";
  showOnline?: boolean;
  isOnline?: boolean;
  showBadge?: boolean;
  badgeCount?: number;
  className?: string;
  onClick?: () => void;
  gradientSeed?: string; // For consistent gradient backgrounds
}

export default function Avatar({
  src,
  alt,
  name = "",
  size = "medium",
  shape = "circle",
  showOnline = false,
  isOnline = false,
  showBadge = false,
  badgeCount = 0,
  className,
  onClick,
  gradientSeed,
}: AvatarProps) {
  const getInitials = (name: string) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const getGradientClass = () => {
    // Use gradientSeed or name to generate consistent gradient
    const seed = String(gradientSeed || name || "default");
    const hash = seed.split("").reduce((acc, char) => char.charCodeAt(0) + acc, 0);
    const gradients = [
      "bg-gradient-to-br from-purple-500 to-purple-700", // gradient1
      "bg-gradient-to-br from-pink-400 to-red-500", // gradient2
      "bg-gradient-to-br from-blue-400 to-cyan-400", // gradient3
      "bg-gradient-to-br from-green-400 to-cyan-400", // gradient4
      "bg-gradient-to-br from-pink-400 to-yellow-300", // gradient5
    ];
    return gradients[hash % gradients.length];
  };

  // Size classes
  const sizeClasses = {
    small: "w-8 h-8",
    medium: "w-10 h-10",
    large: "w-12 h-12",
    xlarge: "w-16 h-16",
  };

  const initialsSizeClasses = {
    small: "text-xs",
    medium: "text-sm font-medium",
    large: "text-base font-medium",
    xlarge: "text-lg font-semibold",
  };

  const onlineIndicatorSizeClasses = {
    small: "w-2 h-2 -right-[1px] -bottom-[1px]",
    medium: "w-2.5 h-2.5 right-0 bottom-0",
    large: "w-3 h-3 right-0 bottom-0",
    xlarge: "w-4 h-4 right-[1px] bottom-[1px]",
  };

  const badgeSizeClasses = {
    small: "w-4 h-4 text-[10px] -top-1 -right-1",
    medium: "w-5 h-5 text-xs -top-[5px] -right-[5px]",
    large: "w-5 h-5 text-xs -top-[2px] -right-[2px]",
    xlarge: "w-7 h-7 text-sm -top-[7px] -right-[7px]",
  };

  const shapeClass = shape === "circle" ? "rounded-full" : "rounded-lg";

  const wrapperClasses = cx(
    "relative inline-block flex-shrink-0",
    sizeClasses[size],
    onClick && "cursor-pointer transition-transform hover:scale-105",
    className
  );

  const placeholderClasses = cx(
    "w-full h-full flex items-center justify-center text-white",
    shapeClass,
    getGradientClass()
  );

  const initialsClasses = cx("select-none", initialsSizeClasses[size]);

  const onlineIndicatorClasses = cx(
    "absolute rounded-full border-2 border-white",
    onlineIndicatorSizeClasses[size],
    isOnline ? "bg-green-500" : "bg-gray-400"
  );

  const badgeClasses = cx(
    "absolute rounded-full bg-red-500 text-white",
    "flex items-center justify-center font-medium",
    "border-2 border-white",
    badgeSizeClasses[size]
  );

  return (
    <div className={wrapperClasses} onClick={onClick}>
      {src ? (
        <Image
          src={src}
          alt={alt || name}
          className={cx("w-full h-full object-cover", shapeClass)}
          wrapClassName={cx("w-full h-full overflow-hidden", shapeClass)}
          defaultImage={
            <div className={placeholderClasses}>
              <span className={initialsClasses}>{getInitials(name)}</span>
            </div>
          }
        />
      ) : (
        <div className={placeholderClasses}>
          <span className={initialsClasses}>{getInitials(name)}</span>
        </div>
      )}

      {showOnline && (
        <div className={onlineIndicatorClasses} />
      )}

      {showBadge && badgeCount > 0 && (
        <div className={badgeClasses}>
          <span className="leading-none">
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        </div>
      )}
    </div>
  );
}